import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { activityApi } from '../services/adminApi';
import { queryKeys } from '../utils/queryKeys';
import type { ActivityStatus } from '../types/activity';

/**
 * Variables for status update mutation
 */
interface UpdateStatusVariables {
  id: string;
  status: ActivityStatus;
  previousStatus?: ActivityStatus;
}

/**
 * Hook to update activity status with cache invalidation
 * @returns Mutation object with mutate function and state
 */
export function useUpdateActivityStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateStatusVariables) => {
      return activityApi.updateActivityStatus(variables.id, variables.status);
    },

    onSuccess: (_data, variables) => {
      // Invalidate and refetch activity detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.activities.detail(variables.id),
      });

      // Invalidate activities list to reflect status change
      queryClient.invalidateQueries({
        queryKey: queryKeys.activities.lists(),
      });

      // Determine success message based on transition
      let successMessage = 'Activity status updated successfully';
      
      if (variables.status === 'published') {
        successMessage = 'Activity published successfully';
      } else if (variables.status === 'archived') {
        successMessage = 'Activity archived successfully';
      } else if (variables.status === 'draft') {
        // Was it restored from archive or unpublished?
        successMessage = variables.previousStatus === 'archived'
          ? 'Activity restored to draft successfully'
          : 'Activity unpublished successfully';
      }

      toast.success(successMessage);
    },

    onError: (error: Error) => {
      // Show error toast
      const errorMessage = error.message || 'Failed to update activity status';
      toast.error(errorMessage);

      console.error('Failed to update activity status:', error);
    },
  });
}
